import type {
  ExternalCourse,
  ExternalSubject,
  WorkExperience,
} from './api'

export type DocumentationAttachment = {
  attachment_file_name: string
  attachment_file_size?: number
}

export type DocumentationRow = {
  type: string
  description: string
  attachments: DocumentationAttachment[]
  required: boolean
  edit: () => void
}

export type DocumentationRowEditCallbacks = {
  editExternalCourse: (course: ExternalCourse) => void
  editExternalSubject: (subject: ExternalSubject) => void
  editWorkExperience: (workExperience: WorkExperience) => void
}

export function buildDocumentationRows(
  externalCourses: ExternalCourse[],
  workExperiences: WorkExperience[],
  callbacks: DocumentationRowEditCallbacks,
): DocumentationRow[] {
  const rows: DocumentationRow[] = []

  for (const course of externalCourses) {
    if (!(course.external_course_system_managed || course.external_course_verified === 2)) {
      rows.push({
        type: 'Course',
        description: course.external_course_name,
        attachments: course.external_course_attachments,
        required: true,
        edit: () => callbacks.editExternalCourse(course),
      })
    }

    for (const subject of course.external_subjects ?? []) {
      if (subject.external_subject_verified === 2 || subject.external_subject_system_managed) continue

      const attachments: DocumentationAttachment[] = [...subject.external_subject_attachments]
      if (subject.external_subject_url) {
        attachments.push({
          attachment_file_name: subject.external_subject_url,
        })
      }

      rows.push({
        type: 'Subject',
        description: subject.external_subject_code,
        attachments,
        required: true,
        edit: () => callbacks.editExternalSubject({
          ...subject,
          external_course_id: course.external_course_id,
        }),
      })
    }
  }

  for (const workExperience of workExperiences) {
    if (workExperience.experience_verified === 2) continue

    rows.push({
      type: 'Experience',
      description: workExperience.experience_short_description,
      attachments: workExperience.experience_attachments,
      required: true,
      edit: () => callbacks.editWorkExperience(workExperience),
    })
  }

  return rows
}

export function hasMissingRequiredAttachments(rows: DocumentationRow[]): boolean {
  return rows.some((row) => row.required && row.attachments.length === 0)
}
